import { useMemo, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { Pill, RequestDialog, announceRequest } from '@/components/csp/CspKit';
import { useCspRequests, useDecideCspRequest, type CspRequest, type CspRequestStatus } from '@/api/csp';
import { fmtDate } from '@/lib/csp';
import { cn } from '@/lib/utils';

/**
 * CSP → Requests. Licence requests awaiting approval and their history. Approving fires the
 * order workflow — a Synnex order (stub until the Synnex API exists) and the Autotask contract
 * change (a plan only, unless CSP_ENABLE_AT_WRITES is on).
 */

type Tab = CspRequestStatus | 'all';
const TABS: { id: Tab; label: string }[] = [
  { id: 'pending', label: 'Pending' }, { id: 'approved', label: 'Approved' }, { id: 'rejected', label: 'Rejected' }, { id: 'all', label: 'All' },
];
const TYPE_LABEL = { new: 'New licence', additional: 'Additional', adjustment: 'Adjustment' } as const;
const STATUS_TONE = { pending: 'warn', approved: 'good', rejected: 'bad' } as const;
const STEP_TONE = { stubbed: 'muted', planned: 'warn', executed: 'good', needs_selection: 'warn', error: 'bad' } as const;

export default function CspRequestsPage() {
  const requests = useCspRequests();
  const [tab, setTab] = useState<Tab>('pending');
  const [creating, setCreating] = useState(false);
  const [deciding, setDeciding] = useState<{ r: CspRequest; action: 'approve' | 'reject' } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const all = requests.data?.requests ?? [];
  const counts = useMemo(() => Object.fromEntries(TABS.map((t) => [t.id, t.id === 'all' ? all.length : all.filter((r) => r.status === t.id).length])) as Record<Tab, number>, [all]);
  const rows = tab === 'all' ? all : all.filter((r) => r.status === tab);
  const canApprove = !!requests.data?.canApprove;

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
      <PageHeader title="Licence requests" count={requests.data ? rows.length : null} isFetching={requests.isFetching}
        subtitle="Additional or new Microsoft licences — approved by management before anything is ordered"
        actions={<Button size="sm" onClick={() => setCreating(true)}><Plus className="mr-1.5 h-3.5 w-3.5" /> New request</Button>}
      />

      {canApprove && (
        <div className="mt-3 rounded-md border border-info/30 bg-info-soft px-3 py-2 text-xs text-info">
          You're an <strong>approver</strong>. Pending requests below are waiting on your decision.
        </div>
      )}

      <div className="mt-3 flex gap-1 rounded-lg bg-muted p-1" style={{ width: 'fit-content' }}>
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={cn('rounded-md px-3 py-1 text-xs font-medium transition-colors',
              tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
            {t.label} <span className={cn('ml-1 tabular-nums', t.id === 'pending' && counts.pending ? 'text-warning' : 'text-muted-foreground')}>{counts[t.id]}</span>
          </button>
        ))}
      </div>

      {requests.isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : requests.error ? (
        <div className="mt-4 text-sm text-destructive">Could not load requests: {requests.error instanceof Error ? requests.error.message : 'unknown error'}</div>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Requested</th>
                <th className="px-4 py-2 font-medium">Customer</th>
                <th className="px-4 py-2 font-medium">Product</th>
                <th className="px-4 py-2 text-right font-medium">Qty</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Requested by</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="w-[1%] px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No {tab === 'all' ? '' : tab} requests.</td></tr>}
              {rows.map((r) => (
                <RequestRow key={r.id} r={r} canApprove={canApprove} open={expanded === r.id}
                  onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                  onDecide={(action) => setDeciding({ r, action })} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RequestDialog open={creating} onOpenChange={setCreating} />
      <DecideDialog value={deciding} onClose={() => setDeciding(null)} />
    </div>
  );
}

function RequestRow({ r, canApprove, open, onToggle, onDecide }: {
  r: CspRequest; canApprove: boolean; open: boolean; onToggle: () => void; onDecide: (a: 'approve' | 'reject') => void;
}) {
  const hasDetail = !!(r.notes || r.workflow || r.decisionComment);
  return (
    <>
      <tr className={cn('hover:bg-muted/30', hasDetail && 'cursor-pointer')} onClick={hasDetail ? onToggle : undefined}>
        <td className="whitespace-nowrap px-4 py-2 tabular-nums">{fmtDate(r.createdAt)}</td>
        <td className="max-w-[220px] truncate px-4 py-2 font-medium text-foreground" title={r.customer}>{r.customer}</td>
        <td className="max-w-[280px] truncate px-4 py-2" title={r.product}>{r.product.replace(/^MSNCE\s*/, '')}{r.term && <span className="ml-1 text-xs text-muted-foreground">({r.term})</span>}</td>
        <td className="px-4 py-2 text-right tabular-nums">
          {r.type === 'adjustment' && r.previousQuantity != null ? <>{r.previousQuantity} → {r.quantity}</> : r.quantity}
        </td>
        <td className="px-4 py-2 text-xs">{TYPE_LABEL[r.type]}</td>
        <td className="px-4 py-2 text-xs">{r.requestedByName || r.requestedBy}</td>
        <td className="px-4 py-2"><Pill tone={STATUS_TONE[r.status]}>{r.status}</Pill></td>
        <td className="whitespace-nowrap px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
          {canApprove && r.status === 'pending' ? (
            <div className="flex justify-end gap-1.5">
              <Button size="sm" className="h-7 text-xs" onClick={() => onDecide('approve')}>Approve</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-destructive" onClick={() => onDecide('reject')}>Reject</Button>
            </div>
          ) : r.status !== 'pending' ? (
            <span className="text-xs text-muted-foreground">{r.decidedByName || r.decidedBy}</span>
          ) : null}
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/20">
          <td colSpan={8} className="space-y-1.5 px-4 py-3 text-xs">
            {r.notes && <div><span className="font-medium text-foreground">Notes:</span> {r.notes}</div>}
            {r.decisionComment && <div><span className="font-medium text-foreground">Decision:</span> {r.decisionComment}{r.decidedAt && ` (${fmtDate(r.decidedAt)})`}</div>}
            {r.workflow && (
              <div className="space-y-1">
                <div className="font-medium text-foreground">Order workflow · {r.workflow.writesEnabled ? 'Autotask writes ON' : 'Autotask writes off (plan only)'}</div>
                {r.workflow.steps.map((s, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Pill tone={STEP_TONE[s.status]}>{s.system === 'autotask' ? 'Autotask' : 'Synnex'} · {s.status.replace('_', ' ')}</Pill>
                    <div className="text-muted-foreground">
                      {s.detail}
                      {s.candidates?.map((c) => (
                        <div key={`${c.contractID}:${c.contractServiceId ?? ''}`}>• {c.contractName ?? `Contract #${c.contractID}`}{c.currentQuantity != null && ` — ${c.currentQuantity} units`}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function DecideDialog({ value, onClose }: { value: { r: CspRequest; action: 'approve' | 'reject' } | null; onClose: () => void }) {
  const decide = useDecideCspRequest();
  const [comment, setComment] = useState('');
  const approve = value?.action === 'approve';

  async function submit() {
    if (!value) return;
    try {
      const r = await decide.mutateAsync({ id: value.r.id, action: value.action, comment });
      onClose();
      setComment('');
      if (approve) announceRequest(r); else toast.success('Request rejected');
    } catch (e) {
      toast.error(`Could not ${value.action}: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }

  return (
    <Dialog open={!!value} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{approve ? 'Approve request' : 'Reject request'}</DialogTitle>
          {value && (
            <p className="text-xs text-muted-foreground">
              {value.r.customer} · {value.r.product} · {value.r.type === 'adjustment' ? `${value.r.previousQuantity ?? '?'} → ${value.r.quantity}` : `+${value.r.quantity}`}
            </p>
          )}
        </DialogHeader>
        <div className="space-y-2 px-6 pb-2 text-xs text-muted-foreground">
          {approve && <p>Approving fires the order workflow: the Synnex order (stubbed) and the Autotask contract change.</p>}
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={approve ? 'Comment (optional)' : 'Reason (optional)'} />
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-6 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant={approve ? 'default' : 'destructive'} onClick={submit} disabled={decide.isPending}>
            {decide.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}{approve ? 'Approve' : 'Reject'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
